import OrderResultProcessor from './order-result-processor.util';
import store from '../../../store/store';
import type { ApiError } from '../../../common/utils/api-utils';
import { MagentoOrderRequest, OrderRequest, OrderStatus } from '../../checkout/shop-checkout.interface';
import { MessageTemplateId } from '../../../common/constants/api-constants';
import { StoreState } from '../../../store/store.interface';
import { createOrder, getOrderId } from '../../checkout/shop-checkout';

/**
 * Class to create an order in Magento and process the result.
 */
export class OrderCreator {
  orderResultProcessor: OrderResultProcessor;

  constructor(store?: string) {
    this.orderResultProcessor = new OrderResultProcessor(store);
  }

  /**
   * Creates an order in Magento.
   *
   * [SHOP-API-REMOVAL][WECOMM-20796] The Shop createOrder path and its
   * `useMagento` parameter have been removed. Magento is the only supported
   * path.
   *
   * @param request
   */
  createOrder(request: OrderRequest | MagentoOrderRequest): void {
    store
      .dispatch(createOrder(request))
      .unwrap()
      .then(
        () => this.handleMagentoOrderCreationResult(true),
        (error: ApiError) => this.handleMagentoOrderCreationResult(false, error)
      );
  }

  /**
   * Handles the result of the order creation in Magento.
   *
   * @private
   */
  private handleMagentoOrderCreationResult(orderCreationSucceeded: boolean, error?: ApiError): void {
    const state = store.getState() as StoreState;
    const orderCreation = state.shopCheckoutState.orderCreation;
    const orderStatus = state.shopCheckoutState.orderStatusCheck?.data?.content?.status;
    const errorMessage = error?.response?.message;

    if (orderStatus === OrderStatus.PENDING) {
      return this.orderResultProcessor.processOrderStatus();
    }

    if (!orderCreation.data?.orderId && orderStatus === OrderStatus.FAILURE) {
      return this.orderResultProcessor.processFailure();
    }

    switch (true) {
      case orderCreationSucceeded:
        return this.orderResultProcessor.processSuccess(getOrderId(state));
      case errorMessage === MessageTemplateId.RMS_ORDER_INSERT_ERROR:
        // user will be redirected to temporary order pending page
        return this.orderResultProcessor.processPartial();
      default:
        return this.orderResultProcessor.processFailure();
    }
  }
}
